import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAppCtx } from "../contexts/AppContext";
import { PeriodFilter, periodParams } from "../components/PeriodFilter";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

function fmt(n, currency = "USD") {
    if (n == null) return "—";
    return `${currency === "USD" ? "$" : currency === "EUR" ? "€" : ""}${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
}

export default function DashboardPage() {
    const { selectedEntity, selectedSegment, entities } = useAppCtx();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState({ from: null, to: null, preset: "this_month" });

    useEffect(() => {
        let cancelled = false;
        async function load() {
            setLoading(true);
            try {
                const params = { ...periodParams(period) };
                if (selectedEntity !== "all") params.entity_id = selectedEntity;
                if (selectedSegment !== "all") params.segment = selectedSegment;
                const { data } = await api.get("/reports/dashboard", { params });
                if (!cancelled) setData(data);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [selectedEntity, selectedSegment, period]);

    const chartData = (data?.pnl_breakdown || []).map((b) => ({
        name: b.segment,
        Sales: b.sales,
        COGS: b.cogs,
        Overheads: b.overheads,
        Net: b.net_profit,
    }));

    const entityLabel =
        selectedEntity === "all" ? "All Entities" : entities.find((e) => e.id === selectedEntity)?.name || "—";

    return (
        <div data-testid="dashboard-page">
            <div className="flex items-baseline justify-between mb-6">
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Overview</div>
                    <h1 className="text-3xl font-semibold tracking-tight">Dashboard</h1>
                </div>
                <div className="text-[12px] text-slate-500 font-mono">
                    {entityLabel} · {selectedSegment === "all" ? "All Segments" : selectedSegment}
                </div>
            </div>

            {loading && <div className="text-slate-500 text-sm" data-testid="dashboard-loading">Loading metrics…</div>}

            <div className="flex flex-wrap items-center gap-3 mb-5" data-testid="dashboard-period">
                <PeriodFilter initialPreset="this_month" onChange={setPeriod}/>
            </div>

            {data && (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
                        <KpiCard label="Receivables (USD/EUR)" value={data.kpis.receivables_total_usd_eur} suffix="net" testid="kpi-receivables" positive />
                        <KpiCard label="Payables (USD/EUR)" value={data.kpis.payables_total_usd_eur} suffix="net" testid="kpi-payables" negative />
                        <KpiCard label="Bank Position" value={data.kpis.bank_total_usd} suffix="USD" testid="kpi-bank" />
                        <KpiCard label="Draft Invoices" value={data.kpis.draft_invoices} suffix="rows" raw testid="kpi-draft" />
                        <KpiCard label="Posted Invoices" value={data.kpis.posted_invoices} suffix="rows" raw testid="kpi-posted" />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                        <div className="lg:col-span-2 bg-white border border-slate-200 p-5">
                            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-4">P&amp;L by Segment</div>
                            <div className="h-72">
                                <ResponsiveContainer>
                                    <BarChart data={chartData}>
                                        <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                                        <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                                        <YAxis tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                                        <Tooltip />
                                        <Legend wrapperStyle={{ fontSize: 11 }} />
                                        <Bar dataKey="Sales" fill="#002FA7" />
                                        <Bar dataKey="COGS" fill="#0EA5E9" />
                                        <Bar dataKey="Overheads" fill="#8B5CF6" />
                                        <Bar dataKey="Net" fill="#059669" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                        <div className="bg-white border border-slate-200 p-5">
                            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-4">Segment Breakdown</div>
                            <table className="w-full text-[12.5px] financial">
                                <thead>
                                    <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                                        <th className="py-2">Segment</th>
                                        <th className="num py-2">Sales</th>
                                        <th className="num py-2">Net</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.pnl_breakdown.map((b) => (
                                        <tr key={b.segment} className="border-b border-slate-100" data-testid={`pnl-row-${b.segment}`}>
                                            <td className="py-2">
                                                <span
                                                    className={`inline-block w-2 h-2 mr-2 ${b.segment === "Voice" ? "bg-sky-500" : "bg-violet-500"}`}
                                                />
                                                {b.segment}
                                                <span className="text-[10px] text-slate-400 ml-2 font-mono">{b.base_currency}</span>
                                            </td>
                                            <td className="num py-2">{fmt(b.sales, b.base_currency)}</td>
                                            <td className={`num py-2 ${b.net_profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmt(b.net_profit, b.base_currency)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

function KpiCard({ label, value, suffix, testid, positive, negative, raw }) {
    return (
        <div className="kpi-card" data-testid={testid}>
            <div className="kpi-label">{label}</div>
            <div className={`kpi-value ${positive ? "text-emerald-600" : negative ? "text-red-600" : ""}`}>
                {raw ? Number(value || 0).toLocaleString() : fmt(value || 0, "USD").replace("$", "")}
                <span className="kpi-suffix">{suffix}</span>
            </div>
        </div>
    );
}
